import {
    fetchPutState,
    GetStateByStateKeyResponse
} from '@iwoca/lapi-client/edge';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import * as IwDataDogLogger from '../../../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { TDeserialisedPerson } from '../../../../modals/idCheckVerificationModal/IdCheckVerificationModal.types';

type TAddPhoneNumberParams = {
    person: TDeserialisedPerson;
    phoneNumber: string;
};

/**
 * Persists a new phone number onto a person in customer state, minting a uid.
 * The SMS send resolves its phone_uid from customer state, so a number must be
 * saved before it can be sent to. Returns the new phone's uid.
 */
export const useAddPhoneNumber = () => {
    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async ({ person, phoneNumber }: TAddPhoneNumberParams) => {
            const customerState =
                queryClient.getQueryData<GetStateByStateKeyResponse>([
                    'customer_state',
                    stateKey
                ]);

            const people = customerState?.data?.application?.people;
            if (!people) {
                throw new Error('Customer state not found');
            }

            const phoneUid = uuidv4();
            const updatedPeople = people.map((p) =>
                p.uid === person.uid
                    ? {
                          ...p,
                          phones: [
                              ...(p.phones ?? []),
                              {
                                  uid: phoneUid,
                                  number: phoneNumber,
                                  type: (p.phones?.length
                                      ? 'secondary'
                                      : 'primary') as 'primary' | 'secondary'
                              }
                          ]
                      }
                    : p
            );

            await fetchPutState({
                stateKey,
                body: {
                    data: {
                        ...customerState!.data,
                        application: {
                            ...customerState!.data.application,
                            people: updatedPeople
                        }
                    }
                }
            });

            return phoneUid;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        },
        onError: (error: Error) => {
            IwDataDogLogger.logError('Failed to add phone number', {
                tags: {
                    reporter: 'useAddPhoneNumber',
                    error_message: error.message
                }
            });
        }
    });

    return {
        addPhoneNumber: mutation.mutateAsync,
        isLoading: mutation.isLoading
    };
};
